"use client";

import React from "react";
import Link, { LinkProps } from "next/link";
import { useSession } from "next-auth/react";
import { useSelect } from "@/context/ContextSelector";
import { addContextToUrl } from "@/utils/contextUrl";
import { isAuthenticated, isRouteMatch } from "@/utils/routes";
import { navGuardStore } from "@/stores/navGuardStore";

/**
 * Wrapper that adds the current virus context in links.
 * <ContextLink href="/explore/all">Explore</ContextLink>
 */
export interface ContextLinkProps extends Omit<LinkProps, "href"> {
  href: string;
  children?: React.ReactNode;
  className?: string;
  target?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  style?: React.CSSProperties;
  /**
   * Skip adding context to this link (useful for external links or special cases)
   */
  skipContext?: boolean;
}

export function ContextLink({
  href,
  children,
  skipContext = false,
  ...props
}: ContextLinkProps) {
  const { contextValue } = useSelect();
  const { data: sessionData } = useSession();

  const hrefWithContext = skipContext
    ? href
    : addContextToUrl(href, contextValue);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const { isGuarded, onIntercept } = navGuardStore.getState();
    if (isGuarded && onIntercept) {
      event.preventDefault();
      onIntercept(hrefWithContext);
      return;
    }

    if (isRouteMatch(href) && !isAuthenticated(sessionData)) {
      event.preventDefault();
      window.location.href = addContextToUrl("/login", contextValue);
      return;
    } else {
      if (props.onClick) {
        props.onClick(event);
      }
    }
  };

  return (
    <Link href={hrefWithContext} {...props} onClick={handleClick}>
      {children}
    </Link>
  );
}

export default ContextLink;
